import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeftIcon, ChevronRightIcon, ArrowLeftIcon,
  AdjustmentsHorizontalIcon, MagnifyingGlassIcon,
  BookOpenIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext';
import { colors } from '../../config/colors';
import AccessOptions from '../access/AccessOptions';
import { useZoomProtection } from '../../hooks/useZoomProtection';

// PDF.js imports
import * as pdfjsLib from 'pdfjs-dist';

// Set worker source to local file
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

const Reader2Mobile = React.memo(({ onClose, onBackToWebsite }) => {
  const navigate = useNavigate();
  const { user, hasPurchased, loading } = useAuth();

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [pdfDocument, setPdfDocument] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [readingProgress, setReadingProgress] = useState(0);
  const [theme, setTheme] = useState('light');
  const [retryCount, setRetryCount] = useState(0);
  const [currentPageImage, setCurrentPageImage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [currentSearchIndex, setCurrentSearchIndex] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchPanel, setShowSearchPanel] = useState(false);
  const [showPageJumpPanel, setShowPageJumpPanel] = useState(false);
  const [pageJumpInput, setPageJumpInput] = useState('');
  const [showAccessOptions, setShowAccessOptions] = useState(false);

  // Zoom protection hook
  const { isZooming, justZoomed, isZoomed } = useZoomProtection();

  // Enhanced 3D page flip states for mobile
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionProgress, setTransitionProgress] = useState(0);
  const [transitionDirection, setTransitionDirection] = useState(null);
  const [pageCurl, setPageCurl] = useState(0);
  const [pageLift, setPageLift] = useState(0);
  const [shadowIntensity, setShadowIntensity] = useState(0);
  const [coverOpen, setCoverOpen] = useState(false);
  const [showBookCover, setShowBookCover] = useState(true);

  const canvasRef = useRef(null);
  const renderTaskRef = useRef(null);
  const renderRetryCountRef = useRef(0);
  const maxRenderRetries = 3;

  // Anti-piracy measures
  useEffect(() => {
    // Disable right-click context menu
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable text selection
    const handleSelectStart = (e) => {
      e.preventDefault();
      return false;
    };

    // Block common keyboard shortcuts
    const handleKeyDown = (e) => {
      // Block F12, Ctrl+Shift+I, Ctrl+Shift+C, Ctrl+U, Ctrl+S
      if (
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'C')) ||
        (e.ctrlKey && e.key === 'u') ||
        (e.ctrlKey && e.key === 's')
      ) {
        e.preventDefault();
        return false;
      }
    };



    // Screenshot attempt detection (basic)
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page hidden - could be screenshot attempt
        console.warn('Page visibility changed - potential screenshot attempt');
      }
    };

    // Add event listeners
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('selectstart', handleSelectStart);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('selectstart', handleSelectStart);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Access control
  useEffect(() => {
    // Wait for auth and the profile to load, otherwise paid users get bounced home
    if (loading) {
      return;
    }

    if (!user || !hasPurchased) {
      navigate('/');
      return;
    }
  }, [user, hasPurchased, loading, navigate]);

  // Auto-open book cover after a short delay
  useEffect(() => {
    if (!isLoading && !error) {
      const timer = setTimeout(() => {
        setCoverOpen(true);
        setTimeout(() => {
          setShowBookCover(false);
        }, 1000);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [isLoading, error]);

  // Handle book cover interaction
  const handleBookOpen = () => {
    setCoverOpen(true);
    setTimeout(() => {
      setShowBookCover(false);
    }, 1000);
  };

  // Load PDF document
  useEffect(() => {
    const loadPdfDocument = async () => {
      try {
        setIsLoading(true);
        setError('');

        const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

        const response = await fetch(`${BACKEND_URL}/api/pdf-content`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          mode: 'cors'
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.success) {
          try {
            const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
            const pdfProxyUrl = `${BACKEND_URL}/api/pdf-proxy`;

            const loadingTask = pdfjsLib.getDocument({
              url: pdfProxyUrl,
              cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/cmaps/`,
              cMapPacked: true,
            });

            const pdf = await loadingTask.promise;
            setPdfDocument(pdf);
            setTotalPages(pdf.numPages);

            const savedProgress = localStorage.getItem('fijoli-reading-progress-2.0');
            if (savedProgress) {
              const progress = parseInt(savedProgress);
              if (progress > 0 && progress <= pdf.numPages) {
                setCurrentPage(progress);
              }
            }
          } catch (pdfError) {
            setTotalPages(data.totalPages || 50);
            setPdfDocument({ fallback: true });

            const savedProgress = localStorage.getItem('fijoli-reading-progress-2.0');
            if (savedProgress) {
              const progress = parseInt(savedProgress);
              if (progress > 0 && progress <= (data.totalPages || 50)) {
                setCurrentPage(progress);
              }
            }
          }
        } else {
          throw new Error(data.error || 'Failed to load PDF');
        }

      } catch (err) {
        setError(`Failed to load book: ${err.message}`);

        if (retryCount < 3) {
          setTimeout(() => {
            setRetryCount(prev => prev + 1);
          }, 2000 * (retryCount + 1));
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadPdfDocument();
  }, [navigate, retryCount]);

  // Render current page
  useEffect(() => {
    let isCancelled = false;
    let currentRenderTask = null;

    const renderPage = async () => {
      if (!pdfDocument || pdfDocument.fallback) return;

      try {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');

        if (currentPage <= totalPages) {
          const page = await pdfDocument.getPage(currentPage);
          const viewport = page.getViewport({ scale: 1.5 });

          canvas.width = viewport.width;
          canvas.height = viewport.height;

          const renderContext = {
            canvasContext: context,
            viewport: viewport
          };

          currentRenderTask = page.render(renderContext);
          await currentRenderTask.promise;

          if (isCancelled) {
            canvas.remove();
            return;
          }

          const imageDataUrl = canvas.toDataURL('image/png');
          setCurrentPageImage(imageDataUrl);
          canvas.remove();
        } else {
          setCurrentPageImage('');
        }

      } catch (err) {
        if (err.name === 'RenderingCancelledException') {
          return;
        }

        if (renderRetryCountRef.current < maxRenderRetries) {
          renderRetryCountRef.current++;
          const retryDelay = Math.pow(2, renderRetryCountRef.current) * 1000;

          setTimeout(() => {
            if (!isCancelled) {
              renderPage();
            }
          }, retryDelay);

          return;
        }

        renderRetryCountRef.current = 0;
        setError(`Failed to render page after ${maxRenderRetries} attempts: ${err.message}`);
      }
    };

    if (pdfDocument && currentPage > 0) {
      renderPage();
    }

    return () => {
      isCancelled = true;

      if (currentRenderTask) {
        try {
          currentRenderTask.cancel();
        } catch (cancelError) {
          // Silent cleanup
        }
      }

      if (renderTaskRef.current) {
        try {
          renderTaskRef.current.cancel();
        } catch (cancelError) {
          // Silent cleanup
        }
        renderTaskRef.current = null;
      }
    };
  }, [pdfDocument, currentPage, totalPages]);

  // Save reading progress
  useEffect(() => {
    if (currentPage > 0 && totalPages > 0) {
      const progress = Math.round((currentPage / totalPages) * 100);
      setReadingProgress(progress);
      localStorage.setItem('fijoli-reading-progress-2.0', currentPage.toString());
    }
  }, [currentPage, totalPages]);

  // Handle browser back button
  useEffect(() => {
    const handlePopState = () => {
      navigate('/');
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [navigate]);

  // Enhanced realistic book page transition function for mobile
  const smoothPageTransition = useCallback((direction, targetPage) => {
    if (isTransitioning) return;

    setIsTransitioning(true);
    setTransitionDirection(direction);
    setTransitionProgress(0);
    setPageCurl(0);
    setPageLift(0);
    setShadowIntensity(0);

    // Simple, smooth animation duration for mobile
    const startTime = performance.now();
    const duration = 600; // Balanced duration for smooth feel

    const animateTransition = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Simple, smooth easing for natural page turning
      const easeProgress = progress < 0.5
        ? 2 * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;

      setTransitionProgress(easeProgress);

      // Simple 3D page curl effect
      if (direction === 'next') {
        setPageCurl(easeProgress);
        setPageLift(Math.sin(easeProgress * Math.PI) * 0.1);
        setShadowIntensity(Math.sin(easeProgress * Math.PI) * 0.4);
      } else {
        setPageCurl(easeProgress);
        setPageLift(Math.sin(easeProgress * Math.PI) * 0.08);
        setShadowIntensity(Math.sin(easeProgress * Math.PI) * 0.3);
      }

      if (progress < 1) {
        requestAnimationFrame(animateTransition);
      } else {
        // Transition complete, update page
        setCurrentPage(targetPage);

        // Simple reset
        setTimeout(() => {
          setIsTransitioning(false);
          setTransitionProgress(0);
          setPageCurl(0);
          setPageLift(0);
          setShadowIntensity(0);
          setTransitionDirection(null);
        }, 16);
      }
    };

    requestAnimationFrame(animateTransition);
  }, [isTransitioning]);

  // Navigation functions - Mobile single page with enhanced transitions
  const nextPage = useCallback(() => {
    if (currentPage < totalPages && !isTransitioning) {
      smoothPageTransition('next', currentPage + 1);
    }
  }, [currentPage, totalPages, isTransitioning, smoothPageTransition]);

  const prevPage = useCallback(() => {
    if (currentPage > 1 && !isTransitioning) {
      smoothPageTransition('prev', currentPage - 1);
    }
  }, [currentPage, isTransitioning, smoothPageTransition]);

  // Search functionality
  const searchInPDF = useCallback(async (query) => {
    if (!pdfDocument || !query.trim()) {
      setSearchResults([]);
      setCurrentSearchIndex(0);
      return;
    }

    setIsSearching(true);
    const results = [];

    try {
      for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        const page = await pdfDocument.getPage(pageNum);
        const textContent = await page.getTextContent();

        const pageText = textContent.items
          .map(item => item.str)
          .join(' ')
          .toLowerCase();

        const queryLower = query.toLowerCase();
        if (pageText.includes(queryLower)) {
          const regex = new RegExp(queryLower, 'gi');
          let match;
          let matchCount = 0;

          while ((match = regex.exec(pageText)) !== null && matchCount < 10) {
            results.push({
              page: pageNum,
              index: match.index,
              text: match[0],
              context: pageText.substring(Math.max(0, match.index - 30), match.index + query.length + 30)
            });
            matchCount++;
          }
        }
      }

      setSearchResults(results);
      setCurrentSearchIndex(0);

      if (results.length > 0) {
        setCurrentPage(results[0].page);
      }
    } catch (error) {
      // Handle search error silently
    } finally {
      setIsSearching(false);
    }
  }, [pdfDocument, totalPages]);



  // Handle search input changes
  const handleSearchChange = useCallback((e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (query.trim()) {
      const timeoutId = setTimeout(() => {
        searchInPDF(query);
      }, 300);

      return () => clearTimeout(timeoutId);
    } else {
      setSearchResults([]);
      setCurrentSearchIndex(0);
    }
  }, [searchInPDF]);

  // Page jump functions
  const handlePageJump = useCallback(() => {
    const pageNumber = parseInt(pageJumpInput);
    if (pageNumber && pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
      setPageJumpInput('');
      setShowPageJumpPanel(false);
    }
  }, [pageJumpInput, totalPages]);

  const handlePageJumpInputChange = useCallback((e) => {
    const value = e.target.value;
    // Only allow numbers
    if (/^\d*$/.test(value)) {
      setPageJumpInput(value);
    }
  }, []);

  const handlePageJumpKeyPress = useCallback((e) => {
    if (e.key === 'Enter') {
      handlePageJump();
    }
  }, [handlePageJump]);

  // Prevent flipbook events during zoom cooldown
  const handleFlipBookTouch = (e) => {
    if (justZoomed || isZooming) {
      e.stopPropagation();
      e.preventDefault();
    }
  };

  // Get current theme colors
  const currentTheme = colors.themes[theme] || colors.themes.light;



  // Anti-piracy: Generate user-specific watermark
  const generateWatermark = useCallback(() => {
    // Use fijoli.com instead of email for watermark
    const timestamp = new Date().toISOString().split('T')[0];
    const watermark = `www.fijoli.com | ${timestamp}`;
    console.log('Generated watermark:', watermark);
    return watermark;
  }, []);

  // Loading state
  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-red-800 text-lg font-semibold">Opening your book...</p>
          <p className="text-red-600 text-sm mt-2">Please wait while we load your reading experience</p>
          {retryCount > 0 && (
            <p className="text-red-600 text-sm mt-2">Retry attempt {retryCount}/3</p>
          )}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-red-600 text-6xl mb-4">📖</div>
          <h2 className="text-2xl font-bold text-red-800 mb-2">Unable to Load Book</h2>
          <p className="text-red-700 mb-4">{error}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {retryCount < 3 && (
              <button
                onClick={() => {
                  setError('');
                  setIsLoading(true);
                  setRetryCount(prev => prev + 1);
                }}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
            )}
            <button
              onClick={() => {
                if (onClose) onClose();
                else if (onBackToWebsite) onBackToWebsite();
                else navigate('/');
              }}
              className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors"
            >
              Return Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 overflow-hidden reader-container"
      style={{
        background: currentTheme.background
      }}
    >
      {/* Enhanced CSS for mobile optimization with anti-piracy */}
      <style jsx>{`
        .reader-container {
          touch-action: manipulation pinch-zoom;
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          user-select: none;
          -webkit-touch-callout: none;
          -webkit-tap-highlight-color: transparent;
        }
        
        /* Ensure pinch zoom works on all child elements */
        .reader-container * {
          touch-action: manipulation pinch-zoom;
        }

        .reader-container * {
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          user-select: none;
          -webkit-touch-callout: none;
        }

        /* Allow text selection only in input fields */
        .reader-container input,
        .reader-container textarea {
          -webkit-user-select: text;
          -moz-user-select: text;
          -ms-user-select: text;
          user-select: text;
          font-size: 16px; /* Prevent zoom on iOS */
        }

        /* Prevent zoom on input focus */
        .reader-container input[type="text"],
        .reader-container input[type="search"],
        .reader-container input[type="number"] {
          font-size: 16px;
          transform: scale(1);
        }

        /* Zoom state styles */
        .zooming {
          touch-action: pinch-zoom !important;
        }
        
        .zoom-cooldown {
          pointer-events: none !important;
          transition: pointer-events 0.3s ease;
        }

        @media (max-width: 768px) {
          button {
            min-height: 44px;
            min-width: 44px;
            touch-action: manipulation;
          }
          
          .settings-panel,
          .search-panel {
            touch-action: manipulation;
          }
          
          .settings-panel button,
          .search-panel button {
            touch-action: manipulation;
          }
        }
      `}</style>

      {/* Hidden canvas for PDF rendering */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      {/* Book Texture Overlay */}
      <div className="absolute inset-0 pointer-events-none opacity-5">
        <div className="w-full h-full" style={{
          backgroundImage: `repeating-linear-gradient(
            45deg,
            transparent,
            transparent 2px,
            rgba(139, 69, 19, 0.1) 2px,
            rgba(139, 69, 19, 0.1) 4px
          )`
        }}></div>
      </div>


      {/* Top Navigation Bar - Book-themed */}
      <div
        className="absolute top-0 left-0 right-0 z-40 h-16 flex items-center justify-between px-2 sm:px-4 shadow-lg backdrop-blur-sm"
        style={{
          backgroundColor: `${currentTheme.surface}CC`,
          borderBottom: `2px solid ${currentTheme.border}`,
          backgroundImage: 'linear-gradient(to right, rgba(139, 69, 19, 0.1), rgba(160, 82, 45, 0.1))'
        }}
      >
        {/* Left side - Back and Title */}
        <div className="flex items-center space-x-2 sm:space-x-4 flex-1 min-w-0">
          <button
            onClick={() => {
              if (onClose) {
                onClose();
              } else if (onBackToWebsite) {
                onBackToWebsite();
              } else {
                navigate('/');
              }
            }}
            className="p-2 rounded-lg hover:bg-opacity-20 transition-colors flex-shrink-0"
            style={{ color: currentTheme.text, backgroundColor: `${colors.primary.main}20` }}
          >
            <ArrowLeftIcon className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
          <div className="min-w-0 flex-1 ml-1">
            <h1 className="text-sm sm:text-lg font-bold truncate" style={{ color: currentTheme.text }}>
              Fijoli Book Reader
              {isZoomed && <span className="ml-2 text-xs opacity-70">🔍</span>}
            </h1>
            <p className="text-xs sm:text-sm opacity-70 truncate" style={{ color: currentTheme.text }}>
              Page {currentPage} of {totalPages} ({readingProgress}% complete)
            </p>
          </div>
        </div>

        {/* Right side - Page Numbers, Settings, Search, and Order Hard Copy */}
        <div className="flex items-center space-x-1 sm:space-x-2 flex-shrink-0">
          {/* Page Numbers and Go To Button */}
          <div className="flex items-center space-x-2 bg-amber-50/95 backdrop-blur-sm rounded-full shadow-lg px-3 py-1">
            <div className="text-center">
              <span className="text-xs font-medium font-serif" style={{ color: '#92400e' }}>
                {currentPage} / {totalPages}
              </span>
            </div>

            <motion.button
              onClick={() => setShowPageJumpPanel(true)}
              whileTap={{ scale: 0.95 }}
              className="px-2 py-1 rounded-full transition-all duration-200 bg-amber-100 hover:bg-amber-200"
              style={{ color: '#92400e' }}
            >
              <span className="text-xs font-medium">Go</span>
            </motion.button>
          </div>

          {/* Order Hard Copy Button - Show for online users who haven't upgraded yet */}
          {user && hasPurchased && !user.hasHardCopy && !user.hasUpgraded && (
            <motion.button
              onClick={() => setShowAccessOptions(true)}
              className="px-3 py-2 rounded-lg transition-all duration-200 bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium shadow-lg"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              title="Order Physical Book"
            >
              📖 Physical Book
            </motion.button>
          )}

          <button
            onClick={() => setShowSearchPanel(!showSearchPanel)}
            className="p-2 rounded-lg hover:bg-opacity-20 transition-colors"
            style={{ color: currentTheme.text, backgroundColor: `${colors.primary.main}20` }}
            title="Search"
          >
            <MagnifyingGlassIcon className="h-4 w-4 sm:h-5 sm:w-5" />
          </button>

          <button
            onClick={() => setShowSettings(!showSettings)}
            className="p-2 rounded-lg hover:bg-opacity-20 transition-colors"
            style={{ color: currentTheme.text, backgroundColor: `${colors.primary.main}20` }}
            title="Settings"
          >
            <AdjustmentsHorizontalIcon className="h-4 w-4 sm:h-5 sm:w-5" />
          </button>
        </div>
      </div>

      {/* Main Book Content - Realistic Book Layout */}
      <div className="pt-16 pb-4 h-full flex items-center justify-center">
        <div
          className={`w-full h-full flex items-center justify-center px-2 sm:px-4 ${isZooming ? 'zooming' : ''} ${justZoomed ? 'zoom-cooldown' : ''}`}
          style={{
            touchAction: isZooming ? 'pinch-zoom' : 'pan-x pan-y pinch-zoom',
            pointerEvents: justZoomed ? 'none' : 'auto'
          }}
        >
          <div
            className="relative w-full h-full flex items-center justify-center overflow-hidden"
            style={{
              touchAction: isZooming ? 'pinch-zoom' : 'pan-x pan-y pinch-zoom'
            }}
            onTouchStart={handleFlipBookTouch}
            onTouchMove={handleFlipBookTouch}
            onTouchEnd={handleFlipBookTouch}
          >
            {/* Book Cover - Show when first loading or when cover is open */}
            {showBookCover && (
              <motion.div
                className="absolute inset-0 flex items-center justify-center z-20"
                initial={{ opacity: 1, scale: 1 }}
                animate={{
                  opacity: coverOpen ? 0 : 1,
                  scale: coverOpen ? 0.8 : 1,
                  rotateY: coverOpen ? -90 : 0
                }}
                transition={{ duration: 0.8, ease: "easeInOut" }}
                style={{ perspective: '1000px' }}
              >
                {/* Book Cover Container */}
                <div className="relative w-80 h-96 max-w-[90vw] max-h-[85vh]">
                  {/* Front Cover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-800 via-orange-800 to-red-900 rounded-lg shadow-2xl transform rotate-3 hover:rotate-6 transition-transform duration-300 cursor-pointer"
                    onClick={handleBookOpen}>
                    {/* Cover Design */}
                    <div className="absolute inset-4 bg-gradient-to-br from-amber-700 to-orange-800 rounded flex flex-col items-center justify-center text-center">
                      <div className="text-6xl mb-4">📖</div>
                      <h1 className="text-3xl font-serif font-bold text-white mb-2">FIJOLI</h1>
                      <p className="text-sm text-amber-200 mb-4 font-serif">The Complete Guide</p>
                      <p className="text-xs text-amber-300 font-serif">Tap to open</p>
                    </div>
                  </div>

                  {/* Book Spine */}
                  <div className="absolute -right-2 top-0 bottom-0 w-3 bg-gradient-to-b from-amber-900 via-orange-800 to-red-900 rounded-r shadow-lg">
                    <div className="absolute inset-0 bg-gradient-to-b from-amber-800 to-orange-700 opacity-60"></div>
                  </div>

                  {/* Book Back Cover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-900 to-orange-800 rounded-lg transform rotate-3 scale-95 opacity-20"></div>

                  {/* 3D Shadow Effects */}
                  <div className="absolute -inset-4 bg-gradient-to-br from-black/20 to-transparent rounded-lg blur-xl opacity-60"></div>
                </div>
              </motion.div>
            )}

            {/* Book Pages - Show after cover is opened */}
            <motion.div
              className="relative w-full h-full flex items-center justify-center"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{
                opacity: coverOpen ? 1 : 0,
                scale: coverOpen ? 1 : 0.8
              }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >

              {currentPageImage ? (
                <div className="relative w-full h-full">
                  {/* Book Page Container with realistic styling */}
                  <div className="relative w-full h-full max-w-4xl max-h-[85vh] mx-auto">
                    {/* Page Shadow and 3D Effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-gray-800/20 to-transparent rounded-lg transform rotate-1 scale-95 opacity-30"></div>



                    {/* Main Page Content */}
                    <motion.div
                      className="relative w-full h-full bg-white rounded-lg shadow-2xl overflow-hidden"
                      style={{
                        filter: theme === 'sepia' ? 'sepia(0.3)' : 'none',
                        transform: `${isTransitioning && transitionDirection === 'next' ?
                          `perspective(1500px) rotateY(${-pageCurl * 30}deg) translateX(${-pageCurl * 40}px) translateY(${pageLift * 50}px)` : ''}
                        ${isTransitioning && transitionDirection === 'prev' ?
                            `perspective(1500px) rotateY(${pageCurl * 30}deg) translateX(${pageCurl * 40}px) translateY(${pageLift * 50}px)` : ''}`,
                        transformOrigin: isTransitioning && transitionDirection === 'next' ? 'left center' : 'right center',
                        boxShadow: isTransitioning ?
                          `0 0 ${50 + shadowIntensity * 80}px rgba(0,0,0,${0.3 + shadowIntensity * 0.5}), 
                         0 ${pageLift * 20}px ${30 + shadowIntensity * 40}px rgba(0,0,0,${0.2 + shadowIntensity * 0.3})` :
                          '0 8px 32px rgba(0,0,0,0.3)',
                        opacity: isTransitioning ? 1 - transitionProgress * 0.3 : 1,
                      }}
                    >
                      {/* Paper Texture Overlay */}
                      <div
                        className="absolute inset-0 pointer-events-none opacity-30 mix-blend-multiply"
                        style={{
                          backgroundImage: `
                          radial-gradient(circle at 20% 20%, rgba(0,0,0,0.02) 1px, transparent 1px),
                          radial-gradient(circle at 40% 40%, rgba(0,0,0,0.015) 0.5px, transparent 0.5px),
                          radial-gradient(circle at 60% 60%, rgba(0,0,0,0.01) 0.8px, transparent 0.8px),
                          radial-gradient(circle at 80% 80%, rgba(0,0,0,0.02) 1.2px, transparent 1.2px),
                          radial-gradient(circle at 30% 70%, rgba(0,0,0,0.01) 0.6px, transparent 0.6px),
                          radial-gradient(circle at 70% 30%, rgba(0,0,0,0.015) 0.9px, transparent 0.9px)
                        `,
                          backgroundSize: '40px 40px, 60px 60px, 80px 80px, 100px 100px, 50px 50px, 70px 70px',
                          backgroundPosition: '0 0, 20px 20px, 40px 40px, 60px 60px, 10px 10px, 30px 30px'
                        }}
                      />
                      {/* Page Image */}
                      <motion.img
                        src={currentPageImage}
                        alt={`Page ${currentPage}`}
                        className="object-contain w-full h-full"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.2 }}
                        key={currentPage} // Force re-render for each page
                      />

                      {/* Page Edge Shadow */}
                      <div className="absolute top-0 right-0 bottom-0 w-2 bg-gradient-to-l from-gray-400/40 to-transparent"></div>
                      <div className="absolute top-0 left-0 bottom-0 w-2 bg-gradient-to-r from-gray-400/40 to-transparent"></div>



                      {/* Page Number */}
                      <div className="absolute bottom-4 right-4 bg-white/80 backdrop-blur-sm px-3 py-1 rounded-full shadow-lg border border-gray-200">
                        <span className="text-sm font-medium text-gray-700">{currentPage}</span>
                      </div>

                      {/* Realistic Bent Corner Effect - Bottom Right */}
                      <div className="absolute bottom-0 right-0 w-20 h-20 pointer-events-none">
                        {/* Main page shadow cast by the curl */}
                        <div
                          className="absolute bottom-0 right-0 w-12 h-12"
                          style={{
                            background: 'radial-gradient(ellipse at 80% 80%, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.08) 30%, rgba(0,0,0,0.03) 60%, transparent 100%)',
                            clipPath: 'polygon(100% 0%, 0% 100%, 100% 100%)'
                          }}
                        />

                        {/* Background shadow (cast on surface beneath) */}
                        <div
                          className="absolute bottom-0 right-0 w-10 h-10"
                          style={{
                            background: 'radial-gradient(ellipse at 70% 70%, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.12) 40%, rgba(0,0,0,0.05) 70%, transparent 100%)',
                            clipPath: 'polygon(100% 0%, 0% 100%, 100% 100%)',
                            transform: 'translate(3px, 3px)'
                          }}
                        />

                        {/* Curled page underside (inner surface) */}
                        <div
                          className="absolute bottom-0 right-0 w-8 h-8"
                          style={{
                            background: 'linear-gradient(135deg, rgba(0,0,0,0.02) 0%, rgba(0,0,0,0.05) 30%, rgba(0,0,0,0.08) 60%, rgba(0,0,0,0.12) 100%)',
                            clipPath: 'polygon(100% 0%, 0% 100%, 100% 100%)',
                            transform: 'translate(1px, 1px)'
                          }}
                        />

                        {/* Curled page top surface with highlight */}
                        <div
                          className="absolute bottom-0 right-0 w-6 h-6"
                          style={{
                            background: 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.2) 30%, rgba(255,255,255,0.1) 60%, transparent 100%)',
                            clipPath: 'polygon(100% 0%, 0% 100%, 100% 100%)',
                            transform: 'translate(0.5px, 0.5px)'
                          }}
                        />

                        {/* Edge highlight for the curl */}
                        <div
                          className="absolute bottom-0 right-0 w-4 h-4"
                          style={{
                            background: 'linear-gradient(135deg, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.3) 50%, transparent 100%)',
                            clipPath: 'polygon(100% 0%, 0% 100%, 100% 100%)',
                            transform: 'translate(-0.5px, -0.5px)'
                          }}
                        />
                      </div>
                    </motion.div>

                    {/* Book Binding Shadow */}
                    <div className="absolute left-0 top-0 bottom-0 w-4 bg-gradient-to-r from-gray-800/30 to-transparent rounded-l-lg"></div>
                  </div>

                  {/* Anti-piracy: User-specific watermark overlay - Bottom center */}
                  <div
                    className="absolute bottom-2 left-1/2 transform -translate-x-1/2 pointer-events-none z-50"
                  >
                    <div
                      className="text-sm select-none"
                      style={{
                        color: '#666666',
                        opacity: 0.6,
                        textShadow: '1px 1px 2px rgba(255,255,255,0.8)',
                        fontSize: '9px',
                        fontWeight: 'bold',
                        letterSpacing: '0.5px'
                      }}
                    >
                      {generateWatermark()}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center p-8">
                  <BookOpenIcon className="h-16 w-16 mx-auto mb-4 opacity-50" style={{ color: currentTheme.text }} />
                  <p className="text-lg opacity-70" style={{ color: currentTheme.text }}>
                    Page {currentPage}
                  </p>
                </div>
              )}

              {/* Enhanced Realistic Page Turn Overlay */}
              {isTransitioning && (
                <motion.div
                  className="absolute inset-0 pointer-events-none z-10"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {/* Page curl overlay - shows the actual page being turned */}
                  <div
                    className="absolute top-0 bottom-0 w-full"
                    style={{
                      background: `linear-gradient(${transitionDirection === 'next' ? '90deg' : '270deg'}, 
                      rgba(0,0,0,0.15) 0%, 
                      rgba(0,0,0,0.08) 40%, 
                      transparent 100%)`,
                      transformOrigin: transitionDirection === 'next' ? 'left center' : 'right center',
                      transform: transitionDirection === 'next'
                        ? `perspective(1200px) rotateY(${-pageCurl * 35}deg) translateX(${-pageCurl * 40}px) translateZ(${-pageCurl * 20}px)`
                        : `perspective(1200px) rotateY(${pageCurl * 35}deg) translateX(${pageCurl * 40}px) translateZ(${pageCurl * 20}px)`,
                      opacity: Math.min(transitionProgress * 1.5, 0.6),
                      zIndex: 15
                    }}
                  />

                  {/* Dynamic shadow that follows the page curl */}
                  <div
                    className="absolute top-0 bottom-0 w-full"
                    style={{
                      background: `radial-gradient(ellipse at ${transitionDirection === 'next' ? 'left' : 'right'} center, 
                      rgba(0,0,0,${0.3 + shadowIntensity * 0.4}) 0%, 
                      rgba(0,0,0,${0.1 + shadowIntensity * 0.2}) 50%, 
                      transparent 100%)`,
                      transformOrigin: transitionDirection === 'next' ? 'left center' : 'right center',
                      transform: transitionDirection === 'next'
                        ? `translateX(${-pageCurl * 30}px) translateZ(${-pageCurl * 15}px)`
                        : `translateX(${pageCurl * 30}px) translateZ(${pageCurl * 15}px)`,
                      opacity: shadowIntensity * 0.8,
                      zIndex: 14
                    }}
                  />
                </motion.div>
              )}

              {/* Close the book pages motion.div */}
            </motion.div>
          </div>
        </div>
      </div>

      {/* Side Navigation Arrows - Book-themed */}
      <div className="fixed left-2 top-1/2 transform -translate-y-1/2 z-40">
        <motion.button
          onClick={prevPage}
          disabled={currentPage <= 1}
          whileTap={{ scale: currentPage > 1 ? 0.95 : 1 }}
          className="p-2 transition-all duration-200 disabled:opacity-30 border-0 outline-none focus:outline-none"
          style={{
            color: currentPage > 1 ? colors.primary.main : currentTheme.text,
            backgroundColor: 'transparent',
            border: 'none',
            outline: 'none'
          }}
        >
          <ChevronLeftIcon className="h-5 w-5" />
        </motion.button>
      </div>

      <div className="fixed right-2 top-1/2 transform -translate-y-1/2 z-40">
        <motion.button
          onClick={nextPage}
          disabled={currentPage >= totalPages}
          whileTap={{ scale: currentPage < totalPages ? 0.95 : 1 }}
          className="p-2 transition-all duration-200 disabled:opacity-30 border-0 outline-none focus:outline-none"
          style={{
            color: currentPage < totalPages ? colors.primary.main : currentTheme.text,
            backgroundColor: 'transparent',
            border: 'none',
            outline: 'none'
          }}
        >
          <ChevronRightIcon className="h-5 w-5" />
        </motion.button>
      </div>





      {/* Settings Panel - Mobile Optimized */}
      <AnimatePresence>
        {showSettings && (
          <>
            {/* Backdrop for click-outside */}
            <motion.div
              className="fixed inset-0 z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowSettings(false)}
            />
            <motion.div
              className="settings-panel fixed top-16 right-2 sm:right-4 bg-white rounded-lg shadow-xl p-4 w-56 sm:w-64 z-50 max-h-[80vh] overflow-y-auto"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              style={{ backgroundColor: currentTheme.surface, border: `1px solid ${currentTheme.border}` }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-bold mb-4" style={{ color: currentTheme.text }}>Settings</h3>

              {/* Theme Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2" style={{ color: currentTheme.text }}>Theme</label>
                <div className="flex space-x-2">
                  {['light', 'dark', 'sepia'].map((themeOption) => (
                    <button
                      key={themeOption}
                      onClick={() => setTheme(themeOption)}
                      className={`px-3 py-1 rounded text-sm transition-colors ${theme === themeOption ? 'ring-2' : ''
                        }`}
                      style={{
                        backgroundColor: theme === themeOption ? colors.primary.main : currentTheme.input.background,
                        color: theme === themeOption ? 'white' : currentTheme.text,
                        borderColor: currentTheme.border
                      }}
                    >
                      {themeOption.charAt(0).toUpperCase() + themeOption.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Page Jump Panel - Mobile Optimized */}
      <AnimatePresence>
        {showPageJumpPanel && (
          <>
            {/* Backdrop for click-outside */}
            <motion.div
              className="fixed inset-0 z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowPageJumpPanel(false)}
            />
            <motion.div
              className="page-jump-panel fixed top-16 right-2 sm:right-4 bg-white rounded-lg shadow-xl p-4 w-64 sm:w-72 z-50 max-h-[80vh] overflow-y-auto"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              style={{ backgroundColor: currentTheme.surface, border: `1px solid ${currentTheme.border}` }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-bold mb-4" style={{ color: currentTheme.text }}>Go to Page</h3>

              {/* Page Jump Input */}
              <div className="relative mb-4">
                <div className="flex">
                  <input
                    type="text"
                    value={pageJumpInput}
                    onChange={handlePageJumpInputChange}
                    onKeyPress={handlePageJumpKeyPress}
                    placeholder={`1-${totalPages}`}
                    className="flex-1 px-3 py-2 rounded-l border text-sm"
                    style={{
                      backgroundColor: currentTheme.input.background,
                      color: currentTheme.input.text,
                      borderColor: currentTheme.input.border
                    }}
                  />
                  <button
                    onClick={handlePageJump}
                    disabled={!pageJumpInput.trim()}
                    className="px-4 py-2 rounded-r border-l-0 border text-sm transition-colors disabled:opacity-50"
                    style={{
                      backgroundColor: colors.primary.main,
                      color: 'white',
                      borderColor: currentTheme.accent
                    }}
                  >
                    Go
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Enter a page number between 1 and {totalPages}
                </p>
              </div>


              {/* Close Button */}
              <button
                onClick={() => setShowPageJumpPanel(false)}
                className="w-full px-4 py-2 rounded text-sm transition-colors"
                style={{
                  backgroundColor: currentTheme.input.background,
                  color: currentTheme.text,
                  borderColor: currentTheme.border
                }}
              >
                Cancel
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Search Panel - Mobile Optimized */}
      <AnimatePresence>
        {showSearchPanel && (
          <>
            {/* Backdrop for click-outside */}
            <motion.div
              className="fixed inset-0 z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowSearchPanel(false)}
            />
            <motion.div
              className="search-panel fixed top-16 right-2 sm:right-4 bg-white rounded-lg shadow-xl p-4 w-72 sm:w-80 z-50 max-h-[80vh] overflow-y-auto"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              style={{ backgroundColor: currentTheme.surface, border: `1px solid ${currentTheme.border}` }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-bold mb-4" style={{ color: currentTheme.text }}>Search</h3>

              {/* Search Input */}
              <div className="relative mb-4">
                <div className="flex">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onKeyPress={(e) => e.key === 'Enter' && searchInPDF(searchQuery)}
                    placeholder="Search in book..."
                    className="flex-1 px-3 py-2 rounded-l border text-sm"
                    style={{
                      backgroundColor: currentTheme.input.background,
                      color: currentTheme.input.text,
                      borderColor: currentTheme.input.border
                    }}
                  />
                  <button
                    onClick={() => searchInPDF(searchQuery)}
                    disabled={!searchQuery.trim() || isSearching}
                    className="px-4 py-2 rounded-r border-l-0 border text-sm transition-colors disabled:opacity-50"
                    style={{
                      backgroundColor: colors.primary.main,
                      color: 'white',
                      borderColor: currentTheme.accent
                    }}
                  >
                    {isSearching ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      '🔍'
                    )}
                  </button>
                </div>
              </div>

              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="max-h-64 overflow-y-auto">
                  <h4 className="text-sm font-medium mb-2" style={{ color: currentTheme.text }}>
                    Found {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}:
                  </h4>
                  <div className="space-y-2">
                    {searchResults.map((result, index) => (
                      <div
                        key={`${result.page}-${index}`}
                        className={`p-3 rounded border cursor-pointer transition-colors ${index === currentSearchIndex ? 'ring-2' : ''
                          }`}
                        style={{
                          backgroundColor: index === currentSearchIndex ? `${colors.primary.main}20` : currentTheme.input.background,
                          borderColor: index === currentSearchIndex ? colors.primary.main : currentTheme.input.border,
                          color: currentTheme.text
                        }}
                        onClick={() => {
                          setCurrentSearchIndex(index);
                          setCurrentPage(result.page);
                        }}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium" style={{ color: currentTheme.accent }}>
                            Page {result.page}
                          </span>
                          <span className="text-xs opacity-70">
                            Result {index + 1}
                          </span>
                        </div>
                        <p className="text-sm opacity-80">
                          ...{result.context}...
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* No Results */}
              {searchQuery && !isSearching && searchResults.length === 0 && (
                <div className="text-center py-4">
                  <div className="text-4xl mb-2">🔍</div>
                  <p className="text-sm opacity-70" style={{ color: currentTheme.text }}>
                    No results found for "{searchQuery}"
                  </p>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Access Options Modal */}
      <AccessOptions
        isOpen={showAccessOptions}
        onClose={() => setShowAccessOptions(false)}
        onAccessGranted={() => {
          // User already has access, just close the modal
          setShowAccessOptions(false);
        }}
      />
    </div>
  );
});

export default Reader2Mobile;
